import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { API_CONFIG, ApiConfig } from '../../api-config';

/**
 * Shape mínima de una opción de select. Se define localmente en lugar de
 * importar de `@ui-library` para mantener la dirección de dependencias entre
 * capas: `shared-data-access` no debe depender de libs de UI.
 *
 * El shape es compatible con `FilterOption` de `@ui-library` (mismo
 * `{ value, label }`), así que el componente puede usar el resultado
 * directo en su `[options]="..."` sin mapear.
 */
export interface FiscalSelectOption {
  value: string;
  label: string;
}

/** Prefix de tipo de persona fiscal usado en empresas (persona moral). */
export const TYPE_PEOPLE_PREFIX_PERSONA_MORAL = 'persona_moral';

/**
 * Servicio de acceso a datos para los catálogos fiscales de empresa.
 *
 * Carga options de selects que dependen del país de la empresa:
 * tipo de contribuyente, categoría fiscal, régimen fiscal, tipo de empresa,
 * tamaño de empresa, actividad económica.
 *
 * Convención de endpoints (mismo patrón que el resto de `/selects/*` del
 * proyecto, ej. `loadProvinces` en `company-basic-info`):
 *   GET {apiUrl}/selects/{recurso}?countryId={countryId}[&…]
 *   → { success: true, data: [{ id, text }] }
 *
 * Cache:
 *   Singleton global (`providedIn: 'root'`) con cache en memoria indexado
 *   por (countryId, cacheKey). Si dos componentes piden el mismo
 *   catálogo para el mismo país, el segundo hit es instantáneo. Si en el
 *   futuro se quiere cache por sesión, se puede refactorizar a scope-feature
 *   sin tocar la API pública.
 */
@Injectable({ providedIn: 'root' })
export class FiscalSelectsService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  /**
   * Cache anidada: countryId -> (cacheKey -> options).
   * Mutable interno, no se expone. Se usa como hot cache entre llamadas.
   */
  private readonly cache = new Map<string, Map<string, FiscalSelectOption[]>>();

  /**
   * Carga las options de un catálogo fiscal para un país dado.
   *
   * Si el (countryId, cacheKey) ya está en cache, devuelve `of(cached)`
   * sin hacer request. Si no, hace GET y guarda en cache.
   *
   * @param countryId  id del país (UUID en el caso actual)
   * @param endpointUrl path del endpoint, sin query string. Ej: '/selects/type-of-taxpayers'
   * @param extraParams query params adicionales (ej. typePeoplePrefix)
   */
  getOptionsForCountry(
    countryId: string,
    endpointUrl: string,
    extraParams?: Record<string, string>,
  ): Observable<FiscalSelectOption[]> {
    if (!countryId) {
      return of([]);
    }

    const cacheKey = this.buildCacheKey(endpointUrl, extraParams);
    const cached = this.cache.get(countryId)?.get(cacheKey);
    if (cached) {
      return of(cached);
    }

    const query = new URLSearchParams({ countryId });
    if (extraParams) {
      for (const [key, value] of Object.entries(extraParams)) {
        if (value != null && String(value).trim() !== '') {
          query.set(key, String(value).trim());
        }
      }
    }

    return this.httpClient
      .get(`${this.apiConfig.apiUrl}${endpointUrl}?${query.toString()}`, {
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((resp) => this.parseSelectResponse(resp.body)),
        tap((options) => {
          this.setCache(countryId, cacheKey, options);
        }),
        catchError(() => {
          return of([]);
        }),
      );
  }

  private buildCacheKey(
    endpointUrl: string,
    extraParams?: Record<string, string>,
  ): string {
    if (!extraParams || Object.keys(extraParams).length === 0) {
      return endpointUrl;
    }
    const sorted = Object.keys(extraParams)
      .sort()
      .map((k) => `${k}=${extraParams[k]}`)
      .join('&');
    return `${endpointUrl}?${sorted}`;
  }

  /**
   * Parsea el body del response (que viene como `text` por la convención
   * del proyecto de hacer JSON.parse manual) y mapea a `FiscalSelectOption[]`.
   *
   * Convención de respuesta: `{ success: true, data: [{ id, text }] }`.
   * Si el shape no matchea, devuelve `[]` sin tirar error.
   */
  private parseSelectResponse(body: unknown): FiscalSelectOption[] {
    if (typeof body !== 'string' || !body) return [];
    try {
      const parsed = JSON.parse(body) as {
        success?: boolean;
        data?: Array<{ id: string; text?: string; label?: string }>;
      };
      if (!parsed || parsed.success !== true || !Array.isArray(parsed.data)) {
        return [];
      }
      return parsed.data.map((item) => ({
        value: item.id,
        label: item.text || item.label || '',
      }));
    } catch {
      return [];
    }
  }

  private setCache(
    countryId: string,
    cacheKey: string,
    options: FiscalSelectOption[],
  ): void {
    let inner = this.cache.get(countryId);
    if (!inner) {
      inner = new Map();
      this.cache.set(countryId, inner);
    }
    inner.set(cacheKey, options);
  }
}
